import { useEffect, useState } from "react";
import {
  MapPin,
  Bot,
  Phone,
  Mic,
  MessageCircle,
  Bell,
  Calendar,
  Zap,
  BarChart3,
  Puzzle,
  FileText,
  Lock,
  Globe,
  Eye,
  Target,
  UserCircle,
  GitBranch,
  Wrench,
  Shield,
  TrendingUp,
} from "lucide-react";
import { useTranslation } from "@thyra/landing-i18n";
import { SectionHeader } from "./ui/SectionHeader";
const AGENT_CREATION_STEPS = [
  { id: "purpose", Icon: Target },
  { id: "persona", Icon: UserCircle },
  { id: "workflow", Icon: GitBranch },
  { id: "tools", Icon: Wrench },
  { id: "boundaries", Icon: Shield },
] as const;

const PRIVACY_ITEMS = [
  { id: "encrypted", Icon: Lock },
  { id: "yourRules", Icon: Shield },
  { id: "youApprove", Icon: UserCircle },
  { id: "gdpr", Icon: FileText },
  { id: "yourData", Icon: Globe },
  { id: "transparent", Icon: Eye },
] as const;

/** Location-pin style agent marker for the edge network map (America, Europe, Asia). */
function AgentMapMarker({ style }: { style: React.CSSProperties }) {
  return (
    <span
      className="absolute -translate-x-1/2 -translate-y-full drop-shadow-sm"
      style={style}
      aria-hidden
    >
      <span className="relative inline-block">
        <MapPin
          className="h-9 w-9 fill-muted stroke-border text-muted-foreground dark:fill-neutral-700 dark:stroke-neutral-600 dark:text-neutral-500"
          strokeWidth={2}
        />
        <span className="absolute inset-x-0 top-0 flex justify-center pt-1.5">
          <Bot
            className="h-4 w-4 text-muted-foreground dark:text-neutral-400"
            strokeWidth={2}
          />
        </span>
      </span>
    </span>
  );
}

/** Step interval for the agent-creation demo (ms). */
const AGENT_CREATION_STEP_MS = 3800;

/**
 * Light-waves style background: soft gradient orbs that drift slowly.
 * Used inside the agent-creation card for an AI-friendly ambient effect.
 */
function AgentCreationLightWaves() {
  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl"
      aria-hidden
    >
      <div
        className="agent-creation-wave agent-creation-wave-1 absolute -left-20 -top-10 h-40 w-40 rounded-full opacity-30 dark:opacity-20"
        style={{
          background:
            "radial-gradient(circle, var(--color-primary) 0%, transparent 70%)",
        }}
      />
      <div
        className="agent-creation-wave agent-creation-wave-2 absolute -right-16 top-1/3 h-32 w-32 rounded-full opacity-25 dark:opacity-15"
        style={{
          background:
            "radial-gradient(circle, var(--color-primary) 0%, transparent 70%)",
        }}
      />
      <div
        className="agent-creation-wave agent-creation-wave-3 absolute bottom-0 left-1/3 h-28 w-28 rounded-full opacity-20 dark:opacity-10"
        style={{
          background:
            "radial-gradient(circle, var(--color-primary) 0%, transparent 70%)",
        }}
      />
    </div>
  );
}

/**
 * Animated agent-creation steps: single-line step nav with icons, light-waves
 * background, and live-chat style content with AI icon and typing feel.
 */
function AgentCreationStepsAnimation() {
  const { t } = useTranslation("sections");
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((i) => (i + 1) % AGENT_CREATION_STEPS.length);
    }, AGENT_CREATION_STEP_MS);
    return () => clearInterval(timer);
  }, []);

  const step = AGENT_CREATION_STEPS[activeIndex];

  return (
    <div
      className="agent-creation-steps relative flex h-[220px] w-full flex-col gap-3 overflow-hidden rounded-xl border border-border bg-card/80 p-4 dark:border-white/10 dark:bg-neutral-900/80"
      aria-hidden
    >
      <AgentCreationLightWaves />

      {/* Step nav: only active step, centered, width follows content */}
      <nav className="relative z-10 flex shrink-0 justify-center py-0.5">
        <span className="inline-flex max-w-full items-center justify-center gap-1.5 rounded-full border border-primary bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary transition-colors duration-300 dark:bg-primary/15 dark:text-primary">
          <step.Icon
            className="h-4 w-4 shrink-0 text-primary"
            strokeWidth={2}
            aria-hidden
          />
          <span>{activeIndex + 1}.</span>
          <span className="whitespace-nowrap">
            {t(`autonomousFeatures.steps.${step.id}.label`)}
          </span>
        </span>
      </nav>

      {/* Content: text only, no icon or animation */}
      <div
        key={step.id}
        className="agent-creation-bubble relative z-10 min-h-[106px] shrink rounded-lg border border-border bg-muted/50 px-4 py-3 dark:border-white/10 dark:bg-neutral-800/60"
        style={{ animation: "agent-creation-bubble-in 0.35s ease-out" }}
      >
        <p className="text-sm text-foreground dark:text-neutral-200">
          {t(`autonomousFeatures.steps.${step.id}.prompt`)}
        </p>
      </div>
    </div>
  );
}

/** Illustration for "Talk to your agent on the go": phone, voice waves, messaging. */
function TalkOnTheGoIllustration() {
  const { t } = useTranslation("sections");
  return (
    <div
      className="relative flex min-h-[200px] w-full flex-1 flex-col items-center justify-center gap-6 p-4 sm:flex-row sm:gap-4"
      aria-hidden
    >
      {/* Soft glow behind center */}
      <div
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
        aria-hidden
      >
        <div
          className="size-32 rounded-full bg-primary/10 dark:bg-primary/20"
          style={{ animation: "on-the-go-glow 3s ease-in-out infinite" }}
        />
      </div>
      {/* Concentric voice waves, centered between phone and agent */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 flex size-24 -translate-x-1/2 -translate-y-1/2 items-center justify-center">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="absolute rounded-full border-2 border-primary/30 dark:border-primary/40"
            style={{
              width: 24 + i * 28,
              height: 24 + i * 28,
              animation: "on-the-go-wave 2.5s ease-in-out infinite",
              animationDelay: `${i * 0.25}s`,
            }}
          />
        ))}
      </div>
      {/* Central: phone ↔ connection ↔ agent */}
      <div className="relative z-10 flex items-center gap-4 sm:gap-6">
        {/* Phone device: smartphone silhouette with "talk" state */}
        <div className="flex flex-col items-center gap-1">
          <div className="flex h-16 w-11 flex-col rounded-[10px] border-2 border-border bg-card shadow-md dark:border-white/15 dark:bg-neutral-800">
            <div className="flex shrink-0 justify-center pt-1.5">
              <div
                className="h-1.5 w-8 rounded-full bg-muted-foreground/40"
                aria-hidden
              />
            </div>
            <div className="flex flex-1 items-center justify-center px-1 pb-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 dark:bg-primary/20">
                <Mic className="h-4 w-4 text-primary" strokeWidth={2} />
              </div>
            </div>
          </div>
          <span className="text-[10px] font-medium text-muted-foreground">
            {t("autonomousFeatures.illustration.you")}
          </span>
        </div>
        {/* Connection line with moving signal dot */}
        <div className="relative h-0.5 w-8 shrink-0 overflow-visible rounded-full bg-gradient-to-r from-primary/40 to-primary/30 dark:from-primary/50 dark:to-primary/40 sm:w-10">
          <div
            className="absolute top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-primary shadow-sm"
            style={{
              animation: "on-the-go-signal 2s ease-in-out infinite",
            }}
          />
        </div>
        {/* Agent (bot) */}
        <div className="flex flex-col items-center gap-1">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-border bg-muted shadow-sm dark:border-white/15 dark:bg-neutral-800"
            style={{
              animation: "compliance-icon-pulse 2s ease-in-out infinite",
            }}
          >
            <Bot
              className="h-6 w-6 text-muted-foreground dark:text-neutral-400"
              strokeWidth={2}
            />
          </div>
          <span className="text-[10px] font-medium text-muted-foreground">
            {t("autonomousFeatures.illustration.agent")}
          </span>
        </div>
      </div>
      {/* Channels: below on mobile, to the right on larger screens */}
      <div className="relative z-10 flex flex-row flex-wrap items-center justify-center gap-2 sm:absolute sm:right-1 sm:top-1/2 sm:flex-col sm:-translate-y-1/2 sm:justify-center md:right-2 lg:right-3">
        <div
          className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 shadow-sm dark:border-white/10 dark:bg-neutral-800"
          style={{ animation: "on-the-go-float 2.8s ease-in-out infinite" }}
        >
          <MessageCircle className="h-4 w-4 text-muted-foreground" />
          <span className="text-[10px] font-medium text-card-foreground">
            {t("autonomousFeatures.illustration.chat")}
          </span>
        </div>
        <div
          className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 shadow-sm dark:border-white/10 dark:bg-neutral-800"
          style={{
            animation: "on-the-go-float 2.8s ease-in-out infinite",
            animationDelay: "0.4s",
          }}
        >
          <Phone className="h-4 w-4 text-muted-foreground" />
          <span className="text-[10px] font-medium text-card-foreground">
            {t("autonomousFeatures.illustration.voice")}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Morning digest & reminders: bell and calendar cues. */
function DigestIllustration() {
  const { t } = useTranslation("sections");
  return (
    <div
      className="flex w-full flex-row items-stretch justify-center gap-3 rounded-xl p-4"
      aria-hidden
    >
      <div
        className="flex min-w-0 flex-1 flex-col items-center justify-center gap-2 rounded-lg bg-muted px-4 py-4 dark:bg-neutral-800"
        style={{ animation: "api-code-pulse 2.2s ease-in-out infinite" }}
      >
        <Bell className="h-8 w-8 shrink-0 text-muted-foreground" />
        <span className="text-xs font-medium text-foreground dark:text-neutral-200">
          {t("autonomousFeatures.illustration.dailyDigest")}
        </span>
      </div>
      <div
        className="flex min-w-0 flex-1 flex-col items-center justify-center gap-2 rounded-lg bg-muted px-4 py-4 dark:bg-neutral-800"
        style={{
          animation: "api-code-pulse 2.2s ease-in-out infinite",
          animationDelay: "0.3s",
        }}
      >
        <Calendar className="h-8 w-8 shrink-0 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">
          {t("autonomousFeatures.illustration.reminders")}
        </span>
      </div>
    </div>
  );
}

export function AutonomousFeaturesSection() {
  const { t } = useTranslation("sections");
  return (
    <section
      id="autonomous-features"
      className="mx-auto w-full max-w-6xl px-4 py-20 sm:py-28 sm:px-6 lg:px-8"
    >
      <SectionHeader
        align="left"
        eyebrow={t("autonomousFeatures.eyebrow")}
        title={t("autonomousFeatures.title")}
        description={t("autonomousFeatures.description")}
      />

      <div className="mx-auto mt-8 grid grid-cols-1 gap-4 md:mt-12 md:grid-cols-3">
        {/* Row 1: 3 cards */}
        {/* Easy agent creation */}
        <div className="marketing-card marketing-card-hover">
          <div className="flex flex-col">
            <div className="flex flex-col gap-2 p-4 sm:p-6">
              <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
                {t("autonomousFeatures.cards.personalize.title")}
              </h3>
              <p className="text-balance text-sm text-neutral-600 dark:text-neutral-400">
                {t("autonomousFeatures.cards.personalize.description")}
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-3 px-4 pb-4 sm:px-6 sm:pb-6">
              <AgentCreationStepsAnimation />
            </div>
          </div>
        </div>

        {/* Agent location on edge network */}
        <div className="marketing-card marketing-card-hover">
          <div className="flex h-full flex-col items-center">
            <div className="flex w-full flex-col gap-2 p-6">
              <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
                {t("autonomousFeatures.cards.fastResponses.title")}
              </h3>
              <p className="text-balance text-sm text-neutral-600 dark:text-neutral-400">
                {t("autonomousFeatures.cards.fastResponses.description")}
              </p>
            </div>
            <div className="relative mt-auto flex flex-1 w-full items-center justify-center overflow-hidden p-2">
              <div className="relative h-full w-full max-w-2xl">
                <img
                  src="/map.svg"
                  alt=""
                  className="h-full w-full object-contain text-neutral-400 dark:invert dark:opacity-90"
                  aria-hidden
                />
                {/* Agent markers: America, Europe, Asia (positions as % of map viewBox 0 0 2000 857) */}
                <AgentMapMarker style={{ left: "19%", top: "35%" }} />
                <AgentMapMarker style={{ left: "55%", top: "31%" }} />
                <AgentMapMarker style={{ left: "79%", top: "37%" }} />
              </div>
            </div>
          </div>
        </div>

        {/* Regulatory compliance */}
        <div className="marketing-card marketing-card-hover">
          <div className="flex h-full flex-col items-center">
            <div className="flex w-full flex-col gap-2 p-6">
              <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
                {t("autonomousFeatures.cards.privacy.title")}
              </h3>
              <p className="text-balance text-sm text-neutral-600 dark:text-neutral-400">
                {t("autonomousFeatures.cards.privacy.description")}
              </p>
            </div>
            <div className="mt-auto flex min-h-44 w-full flex-1 items-center justify-center p-5 sm:p-6">
              <div className="grid h-full w-full grid-cols-3 grid-rows-2 place-items-center gap-x-2 gap-y-5">
                {PRIVACY_ITEMS.map((item, i) => (
                  <div
                    key={item.id}
                    className="group relative flex min-w-0 flex-col items-center gap-2 text-center"
                    title={t(
                      `autonomousFeatures.privacyItems.${item.id}.tooltip`,
                    )}
                  >
                    <div
                      className="flex h-11 w-11 items-center justify-center rounded-full bg-muted transition-colors group-hover:bg-primary/10 dark:bg-neutral-800 dark:group-hover:bg-primary/15"
                      style={{
                        animation:
                          "compliance-icon-pulse 2s ease-in-out infinite",
                        animationDelay: `${i * 0.15}s`,
                      }}
                    >
                      <item.Icon className="h-5 w-5 text-muted-foreground transition-colors group-hover:text-primary" />
                    </div>
                    <span className="max-w-full text-[11px] font-semibold leading-tight text-neutral-700 dark:text-neutral-300">
                      {t(`autonomousFeatures.privacyItems.${item.id}.label`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: 3 cards */}
        {/* Scale from one to many */}
        <div className="group/scale marketing-card marketing-card-hover">
          <div className="flex h-full flex-col p-6">
            <div className="flex items-center gap-3">
              <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[var(--color-primary-muted)] dark:bg-primary/20 overflow-visible">
                <span
                  className="absolute inset-0 rounded-xl border-2 border-primary/30 dark:border-primary/40"
                  style={{
                    animation: "scale-layers-spread 2.5s ease-in-out infinite",
                  }}
                  aria-hidden
                />
                <span
                  className="absolute inset-0 rounded-xl border-2 border-primary/20 dark:border-primary/30"
                  style={{
                    animation: "scale-layers-spread 2.5s ease-in-out infinite",
                    animationDelay: "0.4s",
                  }}
                  aria-hidden
                />
                <TrendingUp
                  className="relative h-6 w-6 text-primary"
                  style={{
                    animation: "scale-layers-pulse 2.2s ease-in-out infinite",
                  }}
                />
              </div>
              {/* One to many: dots replicate */}
              <div className="flex items-center gap-1.5" aria-hidden>
                <span
                  className="h-2 w-2 rounded-full bg-primary/80"
                  style={{ animation: "scale-dot-pop 1.8s ease-out infinite" }}
                />
                <span
                  className="h-2 w-2 rounded-full bg-primary/80"
                  style={{
                    animation: "scale-dot-pop 1.8s ease-out infinite",
                    animationDelay: "0.25s",
                  }}
                />
                <span
                  className="h-2 w-2 rounded-full bg-primary/80"
                  style={{
                    animation: "scale-dot-pop 1.8s ease-out infinite",
                    animationDelay: "0.5s",
                  }}
                />
              </div>
            </div>
            <h3 className="mt-4 text-sm font-semibold text-neutral-900 dark:text-white">
              {t("autonomousFeatures.cards.manyJobs.title")}
            </h3>
            <p className="mt-2 text-balance text-sm text-neutral-600 dark:text-neutral-400">
              {t("autonomousFeatures.cards.manyJobs.description")}
            </p>
          </div>
        </div>

        {/* Talk to your agent on the go */}
        <div className="flex h-full min-h-[22rem] w-full flex-col marketing-card marketing-card-hover">
          <div className="flex flex-col gap-2 p-6">
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
              {t("autonomousFeatures.cards.onTheGo.title")}
            </h3>
            <p className="text-balance text-sm text-neutral-600 dark:text-neutral-400">
              {t("autonomousFeatures.cards.onTheGo.description")}
            </p>
          </div>
          <div className="mt-auto flex flex-1 flex-col items-center justify-center gap-4 p-4">
            <TalkOnTheGoIllustration />
          </div>
        </div>

        {/* Daily digests & reminders */}
        <div className="group/api marketing-card marketing-card-hover relative flex h-full min-h-[22rem] w-full flex-col overflow-hidden">
          <div className="relative z-10 flex flex-1 flex-col">
            <div className="flex flex-col gap-2 p-6">
              <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
                {t("autonomousFeatures.cards.digests.title")}
              </h3>
              <p className="text-balance text-sm text-neutral-600 dark:text-neutral-400">
                {t("autonomousFeatures.cards.digests.description")}
              </p>
            </div>
            <div className="mt-auto flex flex-1 items-center justify-center p-4">
              <DigestIllustration />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom row: 4 small feature cards */}
      <div className="mx-auto mt-4 grid grid-cols-1 gap-4 md:mt-12 md:grid-cols-2 lg:grid-cols-4">
        <div className="group marketing-card marketing-card-hover p-6">
          <Zap className="size-5 text-muted-foreground group-hover:text-primary" />
          <h3 className="mt-4 text-sm font-semibold text-neutral-900 dark:text-white">
            {t("autonomousFeatures.cards.readyInMinutes.title")}
          </h3>
          <p className="mt-2 text-balance text-sm text-neutral-600 dark:text-neutral-400">
            {t("autonomousFeatures.cards.readyInMinutes.description")}
          </p>
        </div>
        <div className="group marketing-card marketing-card-hover p-6">
          <BarChart3
            className="size-5 text-muted-foreground group-hover:text-primary"
            style={{
              animation: "observability-scan 2.5s ease-in-out infinite",
            }}
          />
          <h3 className="mt-4 text-sm font-semibold text-neutral-900 dark:text-white">
            {t("autonomousFeatures.cards.usageCosts.title")}
          </h3>
          <p className="mt-2 text-balance text-sm text-neutral-600 dark:text-neutral-400">
            {t("autonomousFeatures.cards.usageCosts.description")}
          </p>
        </div>
        <div className="group marketing-card marketing-card-hover p-6">
          <Lock
            className="size-5 text-muted-foreground group-hover:text-primary"
            style={{ animation: "secure-shield-pulse 2s ease-in-out infinite" }}
          />
          <h3 className="mt-4 text-sm font-semibold text-neutral-900 dark:text-white">
            {t("autonomousFeatures.cards.approve.title")}
          </h3>
          <p className="mt-2 text-balance text-sm text-neutral-600 dark:text-neutral-400">
            {t("autonomousFeatures.cards.approve.description")}
          </p>
        </div>
        <div className="group marketing-card marketing-card-hover p-6">
          <Puzzle className="size-5 text-muted-foreground group-hover:text-primary" />
          <h3 className="mt-4 text-sm font-semibold text-neutral-900 dark:text-white">
            {t("autonomousFeatures.cards.everydayApps.title")}
          </h3>
          <p className="mt-2 text-balance text-sm text-neutral-600 dark:text-neutral-400">
            {t("autonomousFeatures.cards.everydayApps.description")}
          </p>
        </div>
      </div>
    </section>
  );
}
